import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Cookies from "js-cookie";

// --- IMPORTS ---
import AddCalculatedColumnModal from "./AddCalculatedColumnModal";
import CustomFormRenderer from "./CustomFormRenderer";
import PremiumDatePicker from "./common/PremiumDatePicker";
// import DataEntryList from "./DataEntryList"; // Migrated to DynamicTable
import DynamicTable from "./DynamicTable";
import { flattenComponents, calculateCellValue } from "../utils/formHelpers";

// --- API SERVICE IMPORT ---
import { apiService } from "../services/apiService";
import { useDebounce } from "../hooks/useDebounce";

const FormEntryManager = ({ form, initialEntries, onBack, onUpdateForm }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dateInputRef = useRef(null);
  // --- STATE ---
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // --- PROJECT FILTER STATE (Global for the list) ---
  const [projectList, setProjectList] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");

  const [mixList, setMixList] = useState([]); // Stores list of mix names
  const [selectedMix, setSelectedMix] = useState(""); // Stores selected mix
  const [selectedDate, setSelectedDate] = useState("");

  const [entryMix, setEntryMix] = useState("");
  const [isAddingNewMix, setIsAddingNewMix] = useState(false);
  const [newMixNameInput, setNewMixNameInput] = useState("");
  const [mixError, setMixError] = useState("");

  const [includeHeaderFooter, setIncludeHeaderFooter] = useState(true);

  // --- REF TO TRACK DELETED TEMP IDS ---
  const deletedTempIds = useRef(new Set());

  // Saves as status: 0 (Draft)

  const handleInlineCreate = async (rowData) => {
    const payload = {
      entry_data: {
        ...rowData,
        // Automatically attach headers/footers from the form settings
        session_headers: form.project_details?.headers || [],
        session_footers: form.project_details?.footers || [],
      },
      form: form.id,
      created_by: userName,
      updated_by: userName,
      status: 0, // <--- CRITICAL: Save as Draft
    };

    // --- OPTIMISTIC UPDATE: Add entry to UI immediately ---
    setProjectList((prev) => {
      if (rowData.project_name && !prev.includes(rowData.project_name)) {
        return [...prev, rowData.project_name];
      }
      return prev;
    });

    setMixList((prev) => {
      if (rowData.mix_name && !prev.includes(rowData.mix_name)) {
        return [...prev, rowData.mix_name];
      }
      return prev;
    });

    const tempId = Date.now(); // Temporary ID until server responds
    const optimisticEntry = {
      _rowId: tempId,
      id: tempId,
      ...rowData,
      project_name: rowData.project_name,
      mix_name: rowData.mix_name,
      created_date: rowData.created_date || new Date().toISOString().split("T")[0],
      createdAt: new Date().toISOString(),
      created_by: userName,
      createdBy: userName,
      updatedBy: userName,
      status: 0,
      session_headers: form.project_details?.headers || [],
      session_footers: form.project_details?.footers || [],
    };

    // Add to state immediately (no blink!)
    setEntries((prev) => [optimisticEntry, ...prev]);

    try {
      const response = await apiService.createEntry(payload);
      // FIX: Server returns _rowId as primary ID, check all possible ID fields
      const realId = response ? (response._rowId || response.id || response._id) : null;

      // CHECK: Was this entry deleted while the request was in flight?
      if (deletedTempIds.current.has(tempId)) {
        // Yes, it was deleted. Now we have the REAL ID, so we must delete it from the server.
        if (realId) {
          try {
            await apiService.deleteEntry(realId);
          } catch (deleteErr) {
            console.error("Failed to delete in-flight entry:", deleteErr);
          }
        }
        // Do NOT update state, because it was already removed from UI by confirmDelete
        deletedTempIds.current.delete(tempId); // Cleanup
        return;
      }

      // Update the temporary entry with the real ID from server
      if (realId) {
        setEntries((prev) =>
          prev.map((entry) =>
            entry._rowId === tempId
              ? { ...entry, _rowId: realId, id: realId, _id: realId }
              : entry
          )
        );
      }
    } catch (error) {
      console.error("Inline create failed", error);
      // Remove optimistic entry on failure (unless it was already deleted)
      if (!deletedTempIds.current.has(tempId)) {
        setEntries((prev) => prev.filter((entry) => entry._rowId !== tempId));
      }
    }
  };

  // --- 2. HANDLE INLINE EDIT (The Pencil Icon) ---
  // Updates the row but keeps it as status: 0 (Draft) until submitted
  // --- 2. HANDLE INLINE EDIT (The Pencil Icon) ---
  const handleInlineUpdate = async (updatedRowData) => {
    const rowId =
      updatedRowData.id || updatedRowData._id || updatedRowData._rowId;

    // Find the original row to get its current status
    const originalRow = entries.find((e) => e._rowId === rowId);
    // Use existing status so we don't force it to 'Draft' (0) which shows the Submit button
    const currentStatus = originalRow ? originalRow.status : 1;

    // Optimistic Update
    setEntries((prev) =>
      prev.map((row) =>
        row._rowId === rowId ? { ...row, ...updatedRowData } : row,
      ),
    );

    const payload = {
      entry_data: { ...updatedRowData },
      form: form.id,
      updated_by: userName,
      // CHANGED: Use currentStatus instead of 0.
      // This stops the "Submit All" button from appearing after an edit.
      status: currentStatus,
    };

    try {
      await apiService.updateEntry(rowId, payload);
    } catch (error) {
      console.error("Inline update failed", error);
      refreshData(true); // Revert data if API fails
    }
  };

  // --- 3. HANDLE BATCH SUBMIT (The "Submit All" Button) ---
  const handleBatchSubmit = async () => {
    try {
      const draftCount = entries.filter(e => e.status === 0).length;

      // OPTIMISTIC: Mark all drafts as submitted instantly in UI
      setEntries(prev => prev.map(e =>
        e.status === 0
          ? { ...e, status: 1, remarks: "Batch Submission", updated_by: userName }
          : e
      ));
      showNotification(`New ${draftCount} entries added successfully`);

      // BACKGROUND: Fire API call without blocking
      apiService.submitAllDrafts(form.id, {
        updated_by: userName,
        remarks: "Batch Submission",
      }).then(() => refreshData(true)).catch(err => {
        console.error("Batch submit error:", err);
        refreshData(true); // Revert on failure
      });
    } catch (err) {
      console.error("Batch submit error:", err);
    }
  };
  const handleConfirmNewMix = () => {
    const trimmed = newMixNameInput.trim();
    setMixError("");

    if (trimmed) {
      const exists = mixList.some(
        (m) => m.toLowerCase() === trimmed.toLowerCase(),
      );

      if (exists) {
        setMixError(`"${trimmed}" already exists.`);
        return;
      }

      // Update Local State
      const updatedMixList = [...mixList, trimmed];
      setMixList(updatedMixList);
      setEntryMix(trimmed);
      setIsAddingNewMix(false);
      setNewMixNameInput("");

      // Update LocalStorage (Persist with Form ID)
      try {
        const currentStorage = JSON.parse(
          localStorage.getItem("my_form_mixes") || "[]",
        );
        // ADDED: formId property to link this mix to the current form
        const newMixObj = { id: Date.now(), name: trimmed, formId: form.id };

        localStorage.setItem(
          "my_form_mixes",
          JSON.stringify([...currentStorage, newMixObj]),
        );
      } catch (e) {
        console.error("Failed to save mix to storage", e);
      }
    } else {
      setMixError("Mix name cannot be empty.");
    }
  };

  // --- UPDATED: Load Mixes Filtered by Form ID ---
  useEffect(() => {
    try {
      const savedMixes = localStorage.getItem("my_form_mixes");
      if (savedMixes) {
        const parsedMixes = JSON.parse(savedMixes);

        // ADDED: Filter checks if mix.formId matches the current form.id
        const mixNames = parsedMixes
          .filter((m) => m.formId === form.id && m.name && m.name.trim() !== "")
          .map((m) => m.name);

        setMixList(mixNames);
      }
    } catch (e) {
      console.error("Error loading mixes:", e);
    }
  }, [form?.id]); // Re-run if form ID changes

  // --- NEW: Load Mixes from LocalStorage ---
  useEffect(() => {
    try {
      const savedMixes = localStorage.getItem("my_form_mixes");
      if (savedMixes) {
        const parsedMixes = JSON.parse(savedMixes);
        // Extract names, filter out empty ones
        const mixNames = parsedMixes
          .map((m) => m.name)
          .filter((name) => name && name.trim() !== "");
        setMixList(mixNames);
      }
    } catch (e) {
      console.error("Error loading mixes:", e);
    }
  }, []);

  // This is the filter
  // --- NEW STATE ---
  const [manualLayout, setManualLayout] = useState(null);
  // --- USER SESSION STATE ---
  const [userName, setUserName] = useState(() => Cookies.get("formUser") || "");
  const [isUserSet, setIsUserSet] = useState(() => !!Cookies.get("formUser"));
  const [tempName, setTempName] = useState("");

  // mode variable removed — single unified page now

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [offcanvasMode, setOffcanvasMode] = useState("ADD");
  const [editingEntry, setEditingEntry] = useState(null);
  const [showCalcModal, setShowCalcModal] = useState(false);

  // --- BATCH SUBMIT STATE ---
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [batchRemarks, setBatchRemarks] = useState("");

  // --- NEW: INLINE ERROR STATES ---
  const [projectError, setProjectError] = useState("");
  const [footerError, setFooterError] = useState("");

  // --- IMAGE PREVIEW STATE (Lightbox) ---
  const [previewImage, setPreviewImage] = useState(null);

  // --- ENTRY CONTEXT STATE (Inside Offcanvas) ---
  const [entryProject, setEntryProject] = useState("");
  const [isAddingNewProject, setIsAddingNewProject] = useState(false);
  const [newProjectNameInput, setNewProjectNameInput] = useState("");
  const [entryHeaders, setEntryHeaders] = useState([]);
  const [entryFooters, setEntryFooters] = useState([]);
  const [isEntryConfigLocked, setIsEntryConfigLocked] = useState(false);
  const [includeCreatedBy, setIncludeCreatedBy] = useState(true);
  const [includeCreatedDate, setIncludeCreatedDate] = useState(true);
  const [submissionNotification, setSubmissionNotification] = useState({ message: "", visible: false });

  // --- SEARCHABLE DROPDOWN STATE ---
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const dropdownRef = useRef(null); // Ref for click-outside detection

  const [reportThemeColor, setReportThemeColor] = useState("#a0a0a0ff");
  const [columnOrder, setColumnOrder] = useState([]);
  const [customColumns, setCustomColumns] = useState(
    form?.custom_columns || [],
  );

  const [formKey, setFormKey] = useState(0);
  const formInstanceRef = useRef(null);

  // Default layout from Form definition (Global fallback)
  const [defaultPdfLayout, setDefaultPdfLayout] = useState({
    headers: form?.project_details?.headers || [],
    footers: form?.project_details?.footers || [],
  });

  const [searchTerm, setSearchTerm] = useState("");
  // Debounce search for better performance (300ms delay)
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // --- INITIALIZATION ---
  // Mode sync effect removed to allow browser history to work naturally

  const handleFormReady = useCallback((inst) => {
    formInstanceRef.current = inst;
  }, []);

  // --- CLICK OUTSIDE HANDLER FOR DROPDOWN ---
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProjectDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- DATA FETCHING ---
  // --- DATA FETCHING (Updated to include Mixes) ---
  // isBackground: when true, skip showing loading spinner (for silent syncs after mutations)
  // existingData: if provided, use this data instead of fetching from API
  const refreshData = useCallback(async (isBackground = false, existingData = null) => {
    if (!form?.id) return;

    if (!isBackground && !existingData) setIsLoading(true);
    try {
      const rawData = existingData || await apiService.fetchEntries(form.id);
      const processedData = rawData.map((item) => {
        const innerData = item.entry_data || item.data || {};
        return {
          ...item,
          ...innerData,
          // Use updated_date as the source of truth for "Created Date" if available (Process Submission Date)
          createdAt: item.updated_date || item.created_date,
          updatedAt: item.updated_date,
          createdBy: item.created_by,
          updatedBy: item.updated_by,
        };
      });

      // 1. Extract Unique Projects from Data (Existing Logic)
      const projects = [
        ...new Set(processedData.map((e) => e.project_name).filter(Boolean)),
      ];
      setProjectList(projects);

      // --- NEW LOGIC: Extract Unique Mixes from Data + LocalStorage ---

      // A. Get mixes used in actual entries
      const usedMixes = processedData.map((e) => e.mix_name).filter(Boolean);

      // B. Get mixes saved in LocalStorage (Settings)
      let storedMixes = [];
      try {
        const saved = localStorage.getItem("my_form_mixes");
        if (saved) {
          const parsed = JSON.parse(saved);
          // Filter to only include mixes for THIS form ID
          storedMixes = parsed
            .filter((m) => m.formId === form.id && m.name)
            .map((m) => m.name);
        }
      } catch (e) {
        console.error("Error parsing mixes", e);
      }

      // C. Combine both lists and remove duplicates
      const combinedMixes = [...new Set([...usedMixes, ...storedMixes])];
      setMixList(combinedMixes);
      // ----------------------------------------------------------------

      const sorted = processedData.sort((a, b) => b._rowId - a._rowId);
      setEntries(sorted);
    } catch (error) {
      console.error("Failed to fetch entries:", error);
    } finally {
      if (!isBackground) setIsLoading(false);
    }
  }, [form?.id]);

  useEffect(() => {
    if (form?.id) {
      if (initialEntries && initialEntries.length >= 0) {
        refreshData(true, initialEntries);
      } else {
        refreshData();
      }
    }
  }, [form?.id, initialEntries, refreshData]);

  // Add this useEffect
  useEffect(() => {
    // When project filter changes, clear any manual drag-and-drop overrides
    // so we load fresh data from that project
    setManualLayout(null);
  }, [selectedProject]);

  useEffect(() => {
    if (form?.custom_columns) {
      setCustomColumns(form.custom_columns);
    }
    if (form?.project_details) {
      setDefaultPdfLayout({
        headers: form.project_details.headers || [],
        footers: form.project_details.footers || [],
      });
      if (form.project_details.columnOrder) {
        setColumnOrder(form.project_details.columnOrder);
      }
      if (form.project_details.includeCreatedBy !== undefined) {
        setIncludeCreatedBy(form.project_details.includeCreatedBy);
      }
      if (form.project_details.includeCreatedDate !== undefined) {
        setIncludeCreatedDate(form.project_details.includeCreatedDate);
      }
    }
  }, [form]);

  // --- AUTO-LOAD HEADERS/FOOTERS WHEN PROJECT CHANGES ---
  useEffect(() => {
    // 1. If user has unsaved manual edits (from DynamicTable), use those!
    //    Don't overwrite with old history.
    if (manualLayout) return;

    if (offcanvasMode === "EDIT") return;
    if (!entryProject) return;

    const projectHistory = entries.filter(
      (e) => e.project_name === entryProject,
    );

    if (projectHistory.length > 0) {
      const latestEntry = [...projectHistory].sort(
        (a, b) => b._rowId - a._rowId,
      )[0];
      if (latestEntry) {
        if (latestEntry.session_headers)
          setEntryHeaders(latestEntry.session_headers);
        if (latestEntry.session_footers)
          setEntryFooters(latestEntry.session_footers);
      }
    } else {
      if (form.project_details) {
        setEntryHeaders(form.project_details.headers || []);
        setEntryFooters(form.project_details.footers || []);
      }
    }
  }, [entryProject, offcanvasMode, entries, form, manualLayout]);

  // --- CLEAR ERRORS WHEN INPUTS CHANGE ---
  useEffect(() => {
    if (entryProject || newProjectNameInput) {
      setProjectError("");
    }
  }, [entryProject, newProjectNameInput]);

  // --- FILTER LOGIC ---
  // --- FILTER & SEARCH LOGIC ---
  // --- UPDATED FILTER LOGIC ---
  // --- FILTER LOGIC ---
  const filteredEntries = useMemo(() => {
    let data = entries;

    // 1. Filter by Project
    if (selectedProject) {
      data = data.filter((e) => e.project_name === selectedProject);
    }

    // 2. Filter by Mix
    if (selectedMix) {
      data = data.filter((e) => e.mix_name === selectedMix);
    }

    // --- 3. FILTER BY DATE (This makes the data change) ---
    if (selectedDate) {
      data = data.filter((e) => {
        // Get the date string from your database/API
        const entryDateRaw = e.created_date || e.createdAt;
        if (!entryDateRaw) return false;

        // Convert the entry date to YYYY-MM-DD to compare with the picker
        // Note: using toISOString().split("T")[0] is standard for ISO dates
        const entryDate = new Date(entryDateRaw).toISOString().split("T")[0];

        return entryDate === selectedDate;
      });
    }

    // 4. Filter by Search Term (using debounced value for performance)
    if (debouncedSearchTerm.trim()) {
      const lowerTerm = debouncedSearchTerm.toLowerCase();
      data = data.filter((row) => {
        return Object.values(row).some((val) =>
          String(val).toLowerCase().includes(lowerTerm),
        );
      });
    }
    return data;
  }, [entries, selectedProject, selectedMix, selectedDate, debouncedSearchTerm]);

  // --- SUBMITTED ENTRIES FOR DYNAMIC TABLE (now includes drafts for visibility) ---
  const submittedFilteredEntries = useMemo(() => {
    return filteredEntries;
  }, [filteredEntries]);

  const totalPages = Math.ceil(
    submittedFilteredEntries.length / itemsPerPage
  );
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return submittedFilteredEntries.slice(startIndex, startIndex + itemsPerPage);
  }, [submittedFilteredEntries, currentPage]);


  // Reset page to 1 if Project or Search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedProject, debouncedSearchTerm]);

  const activeLayout = useMemo(() => {
    // 1. PRIORITY: If user dragged/dropped headers in UI, use that immediately
    if (manualLayout) {
      return manualLayout;
    }

    // 2. FALLBACK: Use default form settings (persisted in backend)
    return defaultPdfLayout;
  }, [defaultPdfLayout, manualLayout]);

  // 1. Get ONLY original Form Components (For DataEntryList)
  const originalComponents = useMemo(() => {
    if (!form?.schema?.components) return [];
    return flattenComponents(form.schema.components);
  }, [form]);

  // 2. Get Combined Components (For DynamicTable Report)
  const allComponents = useMemo(() => {
    return [...originalComponents, ...customColumns];
  }, [originalComponents, customColumns]);

  const extractEntryData = (fullEntry) => {
    const metadataKeys = [
      "_rowId",
      "_isDraft",
      "created_by",
      "created_date",
      "updated_date",
      "createdAt",
      "updatedAt",
      "createdBy",
      "status",
      "delete_status",
      "id",
      "form",
      "updatedBy",
      "updated_by",
    ];
    const entryData = {};
    Object.keys(fullEntry).forEach((key) => {
      if (!metadataKeys.includes(key)) {
        entryData[key] = fullEntry[key];
      }
    });
    return entryData;
  };

  const handleUpdateFormConfig = async (updatedData) => {
    try {
      await apiService.updateForm(form.id, updatedData);
      if (onUpdateForm) onUpdateForm();
    } catch (error) {
      console.error("Backend config update failed", error);
    }
  };

  const remarksKey = "remarks";
  const hasRemarksData = entries.some((e) => e.remarks);
  const showRemarksColumn = true;

  useEffect(() => {
    if (allComponents.length > 0) {
      // 1. Get Schema Keys (excluding special ones)
      const schemaKeys = allComponents
        .filter((c) => c.key !== remarksKey)
        .map((c) => c.key);

      // 2. Define System Columns to ENFORCE
      const systemColumns = ["createdBy", "createdAt"];

      setColumnOrder((prev) => {
        // If empty, initialize with schema + system
        if (prev.length === 0) {
          const combined = [...schemaKeys];
          systemColumns.forEach((sc) => {
            if (!combined.includes(sc)) combined.push(sc);
          });
          return combined;
        }

        // If not empty, ENSURE system columns are present AND remove unwanted ones
        let newOrder = [...prev];
        let changed = false;

        // Remove updatedAt if present (legacy)
        if (newOrder.includes("updatedAt")) {
          newOrder = newOrder.filter(c => c !== "updatedAt");
          changed = true;
        }

        systemColumns.forEach((sc) => {
          if (!newOrder.includes(sc)) {
            newOrder.push(sc);
            changed = true;
          }
        });

        return changed ? newOrder : prev;
      });
    }
  }, [allComponents, remarksKey]);

  const listColumnOrder = useMemo(() => {
    const customKeys = customColumns.map((col) => col.key);
    return columnOrder.filter((key) => !customKeys.includes(key));
  }, [columnOrder, customColumns]);

  // --- OFFCANVAS HELPER FUNCTIONS ---
  const updateEntryMetadata = (section, id, newValue) => {
    if (section === "headers") {
      setEntryHeaders((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, value: newValue } : item,
        ),
      );
    } else {
      setEntryFooters((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, value: newValue } : item,
        ),
      );
    }
  };

  // --- NEW: Image Upload with Validation ---
  const handleEntryFooterImageUpload = (id, file) => {
    setFooterError("");
    if (file) {
      // VALIDATION: Check file type strictly
      const validTypes = ["image/jpeg", "image/png", "image/gif", "image/jpg"];
      if (!validTypes.includes(file.type)) {
        setFooterError("Invalid format. Please upload JPG, PNG, or GIF.");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () =>
        updateEntryMetadata("footers", id, reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveFooterImage = (id) => {
    updateEntryMetadata("footers", id, "");
    setFooterError("");
  };

  // --- NEW: Select Project from Searchable Dropdown ---
  const handleSelectProject = (val) => {
    setEntryProject(val);
    setShowProjectDropdown(false);
    setProjectSearch("");
  };

  // --- NEW: Project Confirm with Inline Validation ---
  const handleConfirmNewProject = () => {
    const trimmed = newProjectNameInput.trim();
    setProjectError(""); // Clear old errors

    if (trimmed) {
      // Check for duplicate (case-insensitive)
      const exists = projectList.some(
        (p) => p.toLowerCase() === trimmed.toLowerCase(),
      );

      if (exists) {
        setProjectError(
          `"${trimmed}" already exists. Select it from the dropdown.`,
        );
        return;
      }

      setProjectList((prev) => [...new Set([...prev, trimmed])]);
      setEntryProject(trimmed);
      setIsAddingNewProject(false);
      setNewProjectNameInput("");
    } else {
      setProjectError("Project name cannot be empty.");
    }
  };

  const formatMetadataSummary = (items) => {
    if (!items || items.length === 0) return "None";
    return items
      .filter((i) => i.value && i.value.trim() !== "")
      .map(
        (i) =>
          `${i.label || (i.type === "image" ? "Signature" : "Note")}: ${i.type === "image" ? "[Image]" : i.value}`,
      )
      .join(" | ");
  };

  // --- MAIN ACTIONS ---
  const handleStartSession = () => {
    if (!tempName.trim()) return;
    Cookies.set("formUser", tempName, { expires: 7 });
    setUserName(tempName);
    setIsUserSet(true);
  };

  const handleEditName = () => {
    Cookies.remove("formUser");
    setIsUserSet(false);
    setUserName("");
    setTempName("");
  };

  if (!form)
    return <div className="p-5 text-center text-muted">Loading...</div>;

  if (!isUserSet)
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div
          className="card shadow-sm border-0 rounded-4 p-4"
          style={{ maxWidth: "400px", width: "100%" }}
        >
          <div className="text-center mb-4">
            <div
              className="bg-primary bg-opacity-10 text-primary rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
              style={{ width: "60px", height: "60px" }}
            >
              <i className="bi bi-person-fill fs-3"></i>
            </div>
            <h5 className="fw-bold" style={{ fontSize: "16px" }}>
              Welcome
            </h5>
            <p className="text-muted small " style={{ fontSize: "14px" }}>
              Please enter your name to start.
            </p>
          </div>
          <input
            type="text"
            className="form-control mb-3"
            placeholder="Your Name"
            value={tempName}
            onChange={(e) => setTempName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleStartSession()}
            autoFocus
          />
          <button
            className="btn btn-primary w-100 fw-bold"
            style={{ fontSize: "14px" }}
            onClick={handleStartSession}
            disabled={!tempName.trim()}
          >
            Continue
          </button>
        </div>
      </div>
    );

  const handleNavigationBack = () => {
    onBack?.();
  };

  const openOffcanvas = (entry = null) => {
    setProjectError(""); // Reset errors
    setFooterError("");
    setProjectSearch("");
    setShowProjectDropdown(false);

    if (entry) {
      setEditingEntry(entry);
      setOffcanvasMode("EDIT");

      setEntryProject(entry.project_name || "");
      setEntryMix(entry.mix_name || "");
      // Use entry's saved session data first, then fall back to activeLayout
      // (which reflects real-time DynamicTable edits via manualLayout)
      setEntryHeaders(
        entry.session_headers || activeLayout?.headers || form.project_details.headers || [],
      );
      setEntryFooters(
        entry.session_footers || activeLayout?.footers || form.project_details.footers || [],
      );
      setIsEntryConfigLocked(true);
    } else {
      setEditingEntry(null);
      setOffcanvasMode("ADD");
      setFormKey((p) => p + 1);

      const initialProject = selectedProject || "";
      setEntryProject(initialProject);
      setEntryMix(selectedMix || "");
      // Use activeLayout to reflect any DynamicTable header/footer edits
      setEntryHeaders(activeLayout?.headers || form.project_details.headers || []);
      setEntryFooters(activeLayout?.footers || form.project_details.footers || []);
      setIsEntryConfigLocked(false);
    }
    setShowOffcanvas(true);
  };
  const closeOffcanvas = () => {
    setShowOffcanvas(false);
    setTimeout(() => {
      setEditingEntry(null);
    }, 300);
  };

  const handleRequestDelete = (entry) => {
    setEntryToDelete(entry);
    setShowDeleteModal(true);
  };

  // --- CONFIRM DELETE ---
  const confirmDelete = async () => {
    if (!entryToDelete) return;

    // FIX: Look up the LATEST version of this entry from current state
    // The entryToDelete snapshot may have stale IDs (e.g., still has temp ID
    // while handleInlineCreate already replaced it with the real server _rowId)
    const snapshotId = entryToDelete._rowId || entryToDelete.id || entryToDelete._id;
    const latestEntry = entries.find((e) => {
      return (
        String(e._rowId) === String(snapshotId) ||
        String(e.id) === String(snapshotId) ||
        String(e._id) === String(snapshotId)
      );
    }) || entryToDelete;

    // Use _rowId as the primary identifier (this is what the server uses)
    const idToDelete = latestEntry._rowId;

    if (idToDelete) {
      // Check if this is still a temporary (optimistic) entry that hasn't been saved yet
      const isTempId = typeof idToDelete === 'number' && idToDelete > 1000000000000;

      if (isTempId) {
        // --- CASE A: OPTIMISTIC ENTRY (API create still in flight) ---
        // Mark it so handleInlineCreate will delete from server when it gets the response
        deletedTempIds.current.add(idToDelete);
      } else {
        // --- CASE B: REAL ENTRY (exists on server) ---
        try {
          await apiService.deleteEntry(idToDelete);
        } catch (error) {
          console.error("Delete failed:", error);
          if (!(error.response && error.response.status === 404)) {
            alert("Failed to delete from server. Please refresh the page.");
          }
        }
      }

      // Remove from UI in both cases
      setEntries((prev) => prev.filter((e) => e._rowId !== idToDelete));
    }

    // Close modal
    setShowDeleteModal(false);
    setEntryToDelete(null);
  };

  const handleSaveDraft = () => {
    if (!formInstanceRef.current || isSaving) return;

    // --- INLINE VALIDATION ---
    setProjectError("");
    setMixError(""); // Clear mix error

    let isValid = true;

    if (!entryProject) {
      setProjectError("Please select or create a Project Name.");
      isValid = false;
    }

    // --- NEW: Validate Mix Name ---
    if (!entryMix) {
      setMixError("Please select or create a Mix Name.");
      isValid = false;
    }

    if (!isValid) return; // Stop if validation fails

    setIsSaving(true);
    formInstanceRef.current
      .submit()
      .then(async (submission) => {
        const formData = submission.data;
        const currentEntryData = {
            ...formData,
            project_name: entryProject,
            mix_name: entryMix,
            session_headers: entryHeaders,
            session_footers: entryFooters,
        };
        try {
          if (offcanvasMode === "EDIT") {
            await handleInlineUpdate(currentEntryData);
            showNotification("Entry updated successfully");
          } else {
            const payload = {
              entry_data: currentEntryData,
              form: form.id,
              created_by: userName,
              updated_by: userName,
              status: 0,
            };
            const newRecord = await apiService.createEntry(payload);
            showNotification("New entry added successfully");
            
            // 0. OPTIMISTIC UPDATE: Prepend locally without waiting for fetch
            if (newRecord) {
                setEntries(prev => [newRecord, ...prev]);
            }

            // 1. Reset Form Fields (clears the inputs)
            setFormKey((p) => p + 1);

            // 2. Reset Project Context (Keep it selected)
            // setEntryProject("");

            // 3. Reset Mix Name (Keep it selected)
            // setEntryMix("");

            // 4. Unlock the Configuration (Force the "Setup" card to be EXPANDED/VISIBLE)
            setIsEntryConfigLocked(false);

            // 5. Reset Headers/Footers to latest layout (reflects DynamicTable edits)
            setEntryHeaders(activeLayout?.headers || form.project_details?.headers || []);
            setEntryFooters(activeLayout?.footers || form.project_details?.footers || []);
          }

          // Refresh the background table silently without blocking the UI
          refreshData(true).catch(e => console.error(e));
          
          if (offcanvasMode === "EDIT") {
            closeOffcanvas();
          }
        } catch (error) {
          console.error("Save failed:", error);
          showNotification("Failed to save entry.");
        } finally {
          setIsSaving(false);
        }
      })
      .catch((err) => {
          console.log("Validation failed", err);
          setIsSaving(false);
      });
  };

  const handleRequestSubmitAll = () => {
    setBatchRemarks("");
    setShowSubmitModal(true);
  };

  const showNotification = (message) => {
    setSubmissionNotification({ message, visible: true });
    setTimeout(() => {
      setSubmissionNotification((prev) => ({ ...prev, visible: false }));
    }, 4000);
  };

  const handleFinalSubmitAll = async () => {
    try {
      const draftCount = entries.filter((e) => e.status === 0).length;

      // OPTIMISTIC: Mark all drafts as submitted instantly in UI
      setEntries(prev => prev.map(e =>
        e.status === 0
          ? { ...e, status: 1, remarks: batchRemarks, updated_by: userName }
          : e
      ));
      setShowSubmitModal(false);
      showNotification(`New ${draftCount} entries added successfully`);

      // BACKGROUND: Fire API call without blocking
      apiService.submitAllDrafts(form.id, {
        updated_by: userName,
        remarks: batchRemarks,
      }).then(() => refreshData(true)).catch(err => {
        console.error("Submission failed:", err);
        refreshData(true); // Revert on failure
      });
    } catch (error) {
      console.error("Submission failed:", error);
    }
  };

  const handleCellUpdate = async (rowId, key, newValue) => {
    const entry = entries.find((e) => e._rowId === rowId);
    if (!entry) return;

    // OPTIMISTIC: Update the cell value instantly in UI
    setEntries(prev => prev.map(e =>
      e._rowId === rowId ? { ...e, [key]: newValue } : e
    ));

    const currentEntryData = extractEntryData(entry);
    currentEntryData[key] = newValue;

    // BACKGROUND: Fire API call without blocking
    apiService.updateEntry(rowId, {
      entry_data: currentEntryData,
      updated_by: userName,
      form: form.id,
    }).catch(error => {
      console.error("Cell update failed:", error);
      refreshData(true); // Revert on failure
    });
  };

  const handleAddCustomColumn = async (newCol) => {
    const updatedCols = [...customColumns, newCol];
    setCustomColumns(updatedCols);
    const newOrder = [...columnOrder];
    const createdByIndex = newOrder.findIndex(
      (key) => key === "createdBy" || key === "created_by",
    );
    if (createdByIndex !== -1) {
      newOrder.splice(createdByIndex, 0, newCol.key);
    } else {
      newOrder.push(newCol.key);
    }
    setColumnOrder(newOrder);
    handleUpdateFormConfig({
      custom_columns: updatedCols,
      project_details: {
        ...(manualLayout || defaultPdfLayout),
        columnOrder: newOrder,
        includeCreatedBy,
        includeCreatedDate,
        includeHeaderFooter,
        reportThemeColor
      },
    });
    setShowCalcModal(false);
  };

  const handleDeleteColumn = async (k) => {
    const updatedCols = customColumns.filter((c) => c.key !== k);
    const newOrder = columnOrder.filter((x) => x !== k);
    setCustomColumns(updatedCols);
    setColumnOrder(newOrder);
    handleUpdateFormConfig({
      custom_columns: updatedCols,
      project_details: {
        ...(manualLayout || defaultPdfLayout),
        columnOrder: newOrder,
        includeCreatedBy,
        includeCreatedDate,
        includeHeaderFooter,
        reportThemeColor
      },
    });
  };

  const handleColumnDrop = (draggedCol, targetCol) => {
    if (!draggedCol || draggedCol === targetCol) return;
    const newOrder = [...columnOrder];
    const fromIndex = newOrder.indexOf(draggedCol);
    const toIndex = newOrder.indexOf(targetCol);
    newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, draggedCol);
    setColumnOrder(newOrder);
    handleUpdateFormConfig({
      project_details: {
        ...(manualLayout || defaultPdfLayout),
        columnOrder: newOrder,
        includeCreatedBy,
        includeCreatedDate,
        includeHeaderFooter,
        reportThemeColor
      },
    });
  };

  const handleLayoutChange = (newLayout) => {
    // Always update the manual override so the UI and Exports update immediately
    setManualLayout(newLayout);

    // Save as global default for the form so it persists across reloads
    setDefaultPdfLayout(newLayout);
    handleUpdateFormConfig({
      project_details: { ...newLayout, columnOrder, includeCreatedBy, includeCreatedDate, includeHeaderFooter, reportThemeColor },
    });
  };

  const handleToggleCreatedBy = (val) => {
    setIncludeCreatedBy(val);
    handleUpdateFormConfig({
      project_details: {
        ...(manualLayout || defaultPdfLayout),
        columnOrder,
        includeCreatedBy: val,
        includeCreatedDate,
        includeHeaderFooter,
        reportThemeColor
      },
    });
  };

  const handleToggleCreatedDate = (val) => {
    setIncludeCreatedDate(val);
    handleUpdateFormConfig({
      project_details: {
        ...(manualLayout || defaultPdfLayout),
        columnOrder,
        includeCreatedBy,
        includeCreatedDate: val,
        includeHeaderFooter,
        reportThemeColor
      },
    });
  };

  const handleToggleHeaderFooter = (val) => {
    setIncludeHeaderFooter(val);
    handleUpdateFormConfig({
      project_details: {
        ...(manualLayout || defaultPdfLayout),
        columnOrder,
        includeCreatedBy,
        includeCreatedDate,
        includeHeaderFooter: val,
        reportThemeColor
      },
    });
  };

  const handleThemeColorChange = (val) => {
    setReportThemeColor(val);
    handleUpdateFormConfig({
      project_details: {
        ...(manualLayout || defaultPdfLayout),
        columnOrder,
        includeCreatedBy,
        includeCreatedDate,
        includeHeaderFooter,
        reportThemeColor: val
      },
    });
  };

  const getLabel = (key) => {
    if (key === "updatedAt" || key === "updated_date") return "Updated Date";
    if (key === "createdBy" || key === "created_by") return "Created By";
    if (key === "createdAt" || key === "created_date") return "Created Date";
    const comp = allComponents.find((c) => c.key === key);
    return comp ? comp.label : key;
  };

  const formatExportValue = (val) => {
    if (val === undefined || val === null || val === "") return "-";
    if (Array.isArray(val))
      return val
        .map((item) =>
          typeof item === "object" ? item.label || item.value : item,
        )
        .join(", ");
    if (typeof val === "object") return JSON.stringify(val);
    return val;
  };

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
      ]
      : [160, 160, 160];
  };

  const handleExportExcel = () => {
    const sheetData = [];

    // 1. Report Title
    sheetData.push([form.title || "Form Report"]);
    if (selectedProject) sheetData.push([`Project: ${selectedProject}`]);
    sheetData.push([]);

    // 2. Project Details (Headers) - Only if Checkbox is ON
    if (includeHeaderFooter) {
      sheetData.push(["Project Details"]); // Header Title

      const headers = activeLayout.headers || [];
      for (let i = 0; i < headers.length; i += 2) {
        const r = [];
        // First Column
        if (headers[i]) {
          r.push(headers[i].label || "");
          r.push(headers[i].value || "");
        }
        // Second Column
        if (headers[i + 1]) {
          r.push(""); // Spacer between columns
          r.push(headers[i + 1].label || "");
          r.push(headers[i + 1].value || "");
        }
        sheetData.push(r);
      }
      sheetData.push([]); // Spacer after details
    }

    // --- Footer Details in Grid ---
    if (includeHeaderFooter) {
      const footers = activeLayout.footers || [];
      if (footers.length > 0) {
        sheetData.push(["Footer Details"]);
        for (let i = 0; i < footers.length; i += 2) {
          const r = [];
          if (footers[i]) {
            r.push(footers[i].type === 'image' ? "Signature/Image" : "Note");
            r.push(footers[i].type === 'image' ? (footers[i].value ? "[Image Attached]" : "") : (footers[i].value || ""));
          }
          if (footers[i + 1]) {
            r.push("");
            r.push(footers[i + 1].type === 'image' ? "Signature/Image" : "Note");
            r.push(footers[i + 1].type === 'image' ? (footers[i + 1].value ? "[Image Attached]" : "") : (footers[i + 1].value || ""));
          }
          sheetData.push(r);
        }
        sheetData.push([]); // Spacer
      }
    }

    // 3. Table Headers
    const tableHeader = ["S.No"];
    tableHeader.push("Mix Name"); // Added Mix Name

    columnOrder.forEach((k) => {
      if (!includeCreatedBy && (k === "createdBy" || k === "created_by")) return;
      if (!includeCreatedDate && (k === "createdAt" || k === "created_date")) return;
      tableHeader.push(getLabel(k));
    });

    if (showRemarksColumn) tableHeader.push("Remarks");
    sheetData.push(tableHeader);

    // 4. Table Data
    const submittedEntries = filteredEntries
      .filter((e) => e.status === 1)
      .sort((a, b) => {
        const dateA = new Date(a.createdAt || a.created_date || 0);
        const dateB = new Date(b.createdAt || b.created_date || 0);
        if (dateB - dateA === 0) {
          return (b._rowId || b.id || 0) - (a._rowId || a.id || 0);
        }
        return dateB - dateA;
      });
    submittedEntries.forEach((row, index) => {
      const rData = [index + 1];
      rData.push(row.mix_name || "-"); // Added Mix Name value

      columnOrder.forEach((key) => {
        if (!includeCreatedBy && (key === "createdBy" || key === "created_by")) return;
        if (!includeCreatedDate && (key === "createdAt" || key === "created_date")) return;

        const conf = allComponents.find((c) => c.key === key);
        let val =
          conf?.type === "calculated"
            ? calculateCellValue(row, conf)
            : row[key];

        // Date formatting
        if (
          (key === "updatedAt" ||
            key === "createdAt" ||
            key === "updated_date" ||
            key === "created_date") &&
          val
        ) {
          val = new Date(val).toLocaleString("en-GB");
        }

        // Select/Radio formatting
        if (conf && ["select", "radio", "checkbox"].includes(conf.type)) {
          const options = conf.values || conf.data?.values || [];
          if (options.length > 0) {
            if (Array.isArray(val)) {
              val = val
                .map((v) => {
                  const m = options.find((o) => o.value === v);
                  return m ? m.label : v;
                })
                .join(", ");
            } else {
              const match = options.find((o) => o.value === val);
              if (match) val = match.label;
            }
          }
        }
        rData.push(formatExportValue(val));
      });
      if (showRemarksColumn) rData.push(row[remarksKey] || "-");
      sheetData.push(rData);
    });

    // 5. Write File
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    
    // Robust mobile download
    try {
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${form?.title || "Report"}_${selectedProject || "All"}_${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        if (document.body.contains(link)) document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (e) {
      console.error("Excel download blob failed, falling back", e);
      XLSX.writeFile(
        wb,
        `${form?.title || "Report"}_${selectedProject || "All"}_${Date.now()}.xlsx`,
      );
    }
  };

  const handleExportPDF = () => {
    const doc = new jsPDF("l", "mm", "a4");
    const [r, g, b] = hexToRgb(reportThemeColor);
    doc.setFontSize(18);
    doc.setTextColor(40);
    doc.text(form.title || "Report", 14, 15);

    if (selectedProject) {
      doc.setFontSize(12);
      doc.text(`Project: ${selectedProject}`, 14, 22);
    }

    // Initialize 'y' (Table Start Position) assuming NO headers first
    let y = selectedProject ? 27 : 20;

    // --- 1. HEADER SECTION (Wrapped in Check) ---
    if (includeHeaderFooter) {
      doc.setFillColor(r, g, b);
      doc.rect(14, selectedProject ? 27 : 20, 269, 7, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont(undefined, "bold");
      doc.text("Project Details", 16, selectedProject ? 32 : 25);
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);

      // Reset 'y' to start printing header text below the gray bar
      y = selectedProject ? 42 : 35;

      (activeLayout.headers || []).forEach((h, i) => {
        const xPos = i % 2 === 0 ? 14 : 150;
        if (h.label) {
          doc.setFont(undefined, "bold");
          doc.text(`${h.label}:`, xPos, y);
        }
        doc.setFont(undefined, "normal");
        doc.text(`${h.value || ""}`, xPos + 40, y);

        // Push 'y' down for the next row of headers
        if (i % 2 !== 0) y += 8;
      });
    }
    // ---------------------------------------------

    // --- Table Headers ---
    const pdfHeaders = ["S.No"];
    // Added Mix Name Header
    pdfHeaders.push("Mix Name");

    columnOrder.forEach((k) => {
      if (!includeCreatedBy && (k === "createdBy" || k === "created_by")) return;
      if (!includeCreatedDate && (k === "createdAt" || k === "created_date")) return;
      pdfHeaders.push(getLabel(k));
    });

    if (showRemarksColumn) pdfHeaders.push("Remarks");

    // --- Table Body ---
    const submittedEntries = filteredEntries
      .filter((e) => e.status === 1)
      .sort((a, b) => {
        const dateA = new Date(a.createdAt || a.created_date || 0);
        const dateB = new Date(b.createdAt || b.created_date || 0);
        if (dateB - dateA === 0) {
          return (b._rowId || b.id || 0) - (a._rowId || a.id || 0);
        }
        return dateB - dateA;
      });
    const body = submittedEntries.map((row, index) => {
      const rowData = [index + 1];
      // Added Mix Name Value
      rowData.push(row.mix_name || "-");

      columnOrder.forEach((key) => {
        if (!includeCreatedBy && (key === "createdBy" || key === "created_by")) return;
        if (!includeCreatedDate && (key === "createdAt" || key === "created_date")) return;

        const conf = allComponents.find((c) => c.key === key);
        let val =
          conf?.type === "calculated"
            ? calculateCellValue(row, conf)
            : row[key];
        if (
          (key === "updatedAt" ||
            key === "createdAt" ||
            key === "updated_date" ||
            key === "created_date") &&
          val
        ) {
          val = new Date(val).toLocaleString();
        }
        if (conf && ["select", "radio", "checkbox"].includes(conf.type)) {
          const options = conf.values || conf.data?.values || [];
          if (options.length > 0) {
            if (Array.isArray(val)) {
              val = val
                .map((v) => {
                  const m = options.find((o) => o.value === v);
                  return m ? m.label : v;
                })
                .join(", ");
            } else {
              const match = options.find((o) => o.value === val);
              if (match) val = match.label;
            }
          }
        }
        rowData.push(formatExportValue(val));
      });
      if (showRemarksColumn) rowData.push(row[remarksKey] || "-");
      return rowData;
    });

    // --- Generate Table ---
    autoTable(doc, {
      startY: y + 5, // Uses the 'y' calculated above (either after headers or right after title)
      head: [pdfHeaders],
      body: body,
      headStyles: { fillColor: [r, g, b], textColor: 255 },
      theme: "grid",
      styles: {
        fontSize: 8,
        overflow: "linebreak",
        cellPadding: 2,
        halign: "center",
        valign: "middle",
      },
    });

    // --- 2. FOOTER SECTION (Wrapped in Check) ---
    let finalY = doc.lastAutoTable.finalY + 20;

    if (includeHeaderFooter) {
      const footers = activeLayout.footers || [];
      
      footers.forEach((f, i) => {
        // If pushing down past page bound, add page.
        if (finalY > 180) {
          doc.addPage();
          finalY = 20;
        }

        const xPos = 14;

        if (f.type === "image" && f.value) {
          try {
            doc.setDrawColor(0);
            doc.setLineWidth(0.2);
            doc.line(xPos, finalY + 12, xPos + 60, finalY + 12);
            doc.addImage(f.value, "PNG", xPos, finalY - 5, 40, 15);
          } catch (e) {
            // failed to load image in PDF
          }
        } else if (f.value) {
          doc.setFont(undefined, "bold");
          doc.setFontSize(10);
          doc.text(f.value, xPos, finalY);
        }
        
        // Push finalY down space for images or text rows
        finalY += 20;
      });
    }

    // Robust mobile download
    try {
      const pdfBlob = doc.output("blob");
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${form?.title || "Report"}_${selectedProject || "All"}_${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        if (document.body.contains(link)) document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (e) {
      console.error("PDF download blob failed, falling back", e);
      doc.save(
        `${form?.title || "Report"}_${selectedProject || "All"}_${Date.now()}.pdf`,
      );
    }
  };

  const textStyle = { fontSize: "14px" };
  const headingStyle = { fontSize: "23px", fontWeight: "bold" };
  // --- HELPER: Check if a date is today ---
  const isCreatedToday = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // --- UPDATED hasDrafts LOGIC ---
  // Show "Submit All" if there is a draft that was CREATED TODAY.
  // This assumes edits to old rows (created yesterday) don't need this button.
  const hasDrafts = filteredEntries.some((e) => {
    if (e.status !== 0) return false; // Must be a draft
    return isCreatedToday(e.createdAt || e.created_date);
  });

  return (
    <div className="min-vh-100" style={{ backgroundColor: "#ffffffff" }}>
      <style>{`
                .btn { transition: all 0.2s ease-in-out; }
                .btn:hover { filter: brightness(1.1); transform: translateY(-1px); }
                .btn:active { transform: scale(0.97); }
                .animate-fade-in { animation: fadeIn 0.3s ease-out; }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                
                /* Helper classes for offcanvas layout */
                .setup-summary-line { font-size: 13px; color: #495057; margin-bottom: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .setup-summary-label { font-weight: 700; color: #6c757d; margin-right: 6px; }

                /* --- IMAGE HOVER STYLES FOR FOOTERS --- */
                .img-upload-container {
                    position: relative;
                    display: inline-block;
                }
                .img-upload-container:hover .btn-remove-image {
                    display: flex;
                }
                .btn-remove-image {
                    display: none;
                    position: absolute;
                    top: -8px;
                    right: -8px;
                    width: 20px;
                    height: 20px;
                    background-color: #dc3545;
                    color: white;
                    border-radius: 50%;
                    align-items: center;
                    justify-content: center;
                    font-size: 12px;
                    cursor: pointer;
                    border: 2px solid white;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                }
                .btn-remove-image:hover {
                    background-color: #bb2d3b;
                    transform: scale(1.1);
                }
                
                /* --- CUSTOM SCROLLBAR FOR DROPDOWN --- */
                .custom-scroll::-webkit-scrollbar { width: 5px; }
                .custom-scroll::-webkit-scrollbar-track { background: #f1f1f1; }
                .custom-scroll::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 10px; }
                .custom-scroll::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
                .hover-bg-light:hover { background-color: #f8f9fa; color: #0d6efd; }

/* --- ADD THIS TO FIX DOUBLE ICON ON MOBILE --- */
                input[type="date"]::-webkit-inner-spin-button,
                input[type="date"]::-webkit-calendar-picker-indicator {
                    display: none;
                    -webkit-appearance: none;
                }
                
                /* --- DATE INPUT STYLING --- */

/* Base Style: Ensure the input is wide enough for the text */
input[type="date"] {
    position: relative;
    min-width: 120px;
}

/* Ensure the date text (dd/mm/yyyy) is always visible */
input[type="date"]::-webkit-datetime-edit {
    display: flex !important;
    overflow: visible !important;
}

/* Hide the ugly spinner buttons (up/down arrows) */
input[type="date"]::-webkit-inner-spin-button {
    display: none;
    -webkit-appearance: none;
}

/* --- MOBILE SPECIFIC: "Tap Anywhere" --- */
/* On touch devices, cover the whole input with the picker so it's easy to tap */
@media (pointer: coarse) {
    input[type="date"]::-webkit-calendar-picker-indicator {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        opacity: 0; /* Invisible but clickable */
        cursor: pointer;
        z-index: 10;
        display: block;
    }
}

/* --- DESKTOP SPECIFIC: "Type & Click" --- */
/* On mouse devices, keep the picker on the right so text is editable */
@media (pointer: fine) {
    input[type="date"]::-webkit-calendar-picker-indicator {
        opacity: 0; /* Hide the native icon visually to avoid double icons */
        cursor: pointer;
        /* It stays on the right side by default, allowing you to click/type on the left */
        padding: 5px; /* Make the click target slightly larger on the right */
    }
}

            `}</style>

      <AddCalculatedColumnModal
        show={showCalcModal}
        onClose={() => setShowCalcModal(false)}
        onSave={handleAddCustomColumn}
        existingColumns={allComponents.filter(
          (c) => c.key !== remarksKey,
        )}
      />

      <div
        className={`offcanvas offcanvas-end ${showOffcanvas ? "show" : ""}`}
        style={{
          width: "500px",
          zIndex: 1045,
        }}
        tabIndex="-1"
      >
        <div className="offcanvas-header border-bottom py-2">
          <h6 className="offcanvas-title fw-bold" style={{ fontSize: "15px" }}>
            {offcanvasMode === "EDIT" ? "Edit Entry" : "New Entry"}
          </h6>
          <button
            type="button"
            className="btn-close text-reset"
            onClick={closeOffcanvas}
          ></button>
        </div>
        <div className="offcanvas-body bg-light p-3">
          {/* --- PROJECT CONTEXT SETUP CARD --- */}
          <div className="card mb-4 border shadow-sm">
            <div className="card-header bg-white py-2 d-flex justify-content-between align-items-center">
              <span
                className="small fw-bold text-uppercase text-dark"
                style={{ fontSize: "13px" }}
              >
                Project Context
              </span>
              {entryProject && (
                <button
                  className="btn btn-sm btn-link text-decoration-none p-0"
                  onClick={() => setIsEntryConfigLocked(!isEntryConfigLocked)}
                >
                  {isEntryConfigLocked ? "Change Setup" : "Hide Setup"}
                </button>
              )}
            </div>
            <div className="card-body">
              {!isEntryConfigLocked ? (
                <div className="animate-fade-in">
                  {/* --- 1. PROJECT NAME SECTION (Updated Size) --- */}
                  <div className="mb-4">
                    <label
                      className="form-label fw-bold text-dark"
                      style={{ fontSize: "13px" }}
                    >
                      Project Name
                    </label>
                    <div className="d-flex flex-column gap-1">
                      <div className="d-flex gap-2" ref={dropdownRef}>
                        {isAddingNewProject ? (
                          <div className="input-group">
                            <input
                              type="text"
                              className={`form-control ${projectError ? "border-danger" : ""}`}
                              placeholder="New Project..."
                              value={newProjectNameInput}
                              onChange={(e) =>
                                setNewProjectNameInput(e.target.value)
                              }
                              list="existingProjects"
                              style={{ fontSize: "15px" }}
                            />
                            <datalist id="existingProjects">
                              <option value="General" />
                              {projectList.map((p, i) => (
                                <option key={i} value={p} />
                              ))}
                            </datalist>

                            <button
                              className="btn btn-success"
                              onClick={handleConfirmNewProject}
                            >
                              <i className="bi bi-check fs-5"></i>
                            </button>
                            <button
                              className="btn btn-outline-secondary"
                              onClick={() => {
                                setIsAddingNewProject(false);
                                setProjectError("");
                              }}
                            >
                              <i className="bi bi-x fs-5"></i>
                            </button>
                          </div>
                        ) : (
                          <div className="input-group w-100">
                            <div
                              className={`form-control form-select d-flex justify-content-between align-items-center ${projectError ? "border-danger" : ""
                                }`}
                              onClick={() => {
                                setShowProjectDropdown(!showProjectDropdown);
                                setProjectSearch("");
                              }}
                              style={{
                                cursor: "pointer",
                                backgroundColor: "#fff",
                                position: "relative",
                                fontSize: "15px",
                              }}
                            >
                              <span className="text-truncate">
                                {entryProject || "-- Select Project --"}
                              </span>
                            </div>

                            {showProjectDropdown && (
                              <div
                                className="card position-absolute w-100 shadow mt-1 p-2 border-0"
                                style={{
                                  zIndex: 1050,
                                  top: "100%",
                                  left: 0,
                                  maxHeight: "250px",
                                }}
                              >
                                <input
                                  type="text"
                                  className="form-control mb-2"
                                  placeholder="Search projects..."
                                  value={projectSearch}
                                  onChange={(e) =>
                                    setProjectSearch(e.target.value)
                                  }
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ fontSize: "14px" }}
                                />
                                <ul
                                  className="list-unstyled mb-0 custom-scroll"
                                  style={{
                                    maxHeight: "180px",
                                    overflowY: "auto",
                                  }}
                                >
                                  <li
                                    className="p-2 rounded hover-bg-light"
                                    onClick={() =>
                                      handleSelectProject("General")
                                    }
                                    style={{
                                      cursor: "pointer",
                                      fontSize: "15px",
                                    }}
                                  >
                                    <i className="bi bi-hash me-2 text-muted"></i>
                                    General
                                  </li>
                                  {projectList
                                    .filter(
                                      (p) =>
                                        p
                                          .toLowerCase()
                                          .includes(
                                            projectSearch.toLowerCase(),
                                          ) && p !== "General",
                                    )
                                    .map((p, i) => (
                                      <li
                                        key={i}
                                        className="p-2 rounded hover-bg-light"
                                        onClick={() => handleSelectProject(p)}
                                        style={{
                                          cursor: "pointer",
                                          fontSize: "15px",
                                        }}
                                      >
                                        <i className="bi bi-building me-2 text-muted"></i>
                                        {p}
                                      </li>
                                    ))}
                                  {projectList.length === 0 && (
                                    <li className="p-2 text-muted text-center">
                                      No projects found
                                    </li>
                                  )}
                                </ul>
                              </div>
                            )}

                            <button
                              className="btn btn-outline-primary"
                              onClick={() => setIsAddingNewProject(true)}
                              title="Create New"
                            >
                              <i className="bi bi-plus-lg"></i>
                            </button>
                          </div>
                        )}
                      </div>
                      {projectError && (
                        <small className="text-danger fw-bold">
                          {projectError}
                        </small>
                      )}
                    </div>
                  </div>

                  {/* --- 2. MIX NAME SECTION (Updated Size) --- */}
                  <div className="mb-3">
                    <label
                      className="form-label fw-bold text-dark"
                      style={{ fontSize: "13px" }}
                    >
                      Mix Name
                    </label>
                    <div className="d-flex flex-column gap-1">
                      <div className="d-flex gap-2">
                        {isAddingNewMix ? (
                          <div className="input-group">
                            <input
                              type="text"
                              className={`form-control ${mixError ? "border-danger" : ""}`}
                              placeholder="New Mix Name..."
                              value={newMixNameInput}
                              onChange={(e) =>
                                setNewMixNameInput(e.target.value)
                              }
                              style={{ fontSize: "15px" }}
                            />
                            <button
                              className="btn btn-success"
                              onClick={handleConfirmNewMix}
                            >
                              <i className="bi bi-check fs-5"></i>
                            </button>
                            <button
                              className="btn btn-outline-secondary"
                              onClick={() => setIsAddingNewMix(false)}
                            >
                              <i className="bi bi-x fs-5"></i>
                            </button>
                          </div>
                        ) : (
                          <div className="input-group w-100">
                            <select
                              className="form-select"
                              value={entryMix}
                              onChange={(e) => setEntryMix(e.target.value)}
                              style={{ fontSize: "15px" }}
                            >
                              <option value="">-- Select Mix --</option>
                              {mixList.map((m, i) => (
                                <option key={i} value={m}>
                                  {m}
                                </option>
                              ))}
                            </select>
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => setIsAddingNewMix(true)}
                              title="Create New Mix"
                            >
                              <i className="bi bi-plus-lg"></i>
                            </button>
                          </div>
                        )}
                      </div>
                      {mixError && (
                        <small className="text-danger fw-bold">
                          {mixError}
                        </small>
                      )}
                    </div>
                  </div>

                  {/* Editable Headers */}
                  {entryProject && entryHeaders.length > 0 && (
                    <div className="mb-3 bg-light p-2 rounded border d-none d-lg-block">
                      <h6 className="small fw-bold mb-2 text-muted">Headers</h6>
                      {entryHeaders.map((h) => (
                        <div className="row g-1 mb-1" key={h.id}>
                          <div className="col-4 small text-truncate align-self-center">
                            {h.label}:
                          </div>
                          <div className="col-8">
                            <input
                              className="form-control form-control-sm"
                              value={h.value || ""}
                              onChange={(e) =>
                                updateEntryMetadata(
                                  "headers",
                                  h.id,
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Editable Footers (UPDATED WITH IMAGE UPLOAD PREVIEW) */}
                  {entryProject && entryFooters.length > 0 && (
                    <div className="mb-2 bg-light p-2 rounded border d-none d-lg-block">
                      <h6 className="small fw-bold mb-2 text-muted">Footers</h6>
                      {entryFooters.map((f) => (
                        <div className="row g-1 mb-1" key={f.id}>
                          <div className="col-4 small align-self-center">
                            {f.type === "image" ? "Sig/Img" : "Note"}:
                          </div>
                          <div className="col-8">
                            {f.type === "image" ? (
                              <div className="d-flex flex-column gap-2">
                                {/* Always show preview if value exists */}
                                {f.value && (
                                  <div className="img-upload-container">
                                    <img
                                      src={f.value}
                                      alt="Uploaded"
                                      className="border rounded p-1 bg-white"
                                      style={{
                                        height: "30px",
                                        cursor: "zoom-in",
                                        objectFit: "contain",
                                      }}
                                      onClick={() => setPreviewImage(f.value)}
                                    />
                                    <div
                                      className="btn-remove-image"
                                      onClick={() =>
                                        handleRemoveFooterImage(f.id)
                                      }
                                      title="Remove Image"
                                    >
                                      <i className="bi bi-x"></i>
                                    </div>
                                  </div>
                                )}
                                {/* Always show input to allow uploading or changing image */}
                                <input
                                  type="file"
                                  className="form-control form-control-sm footer-input-sm"
                                  onChange={(e) =>
                                    handleEntryFooterImageUpload(
                                      f.id,
                                      e.target.files[0],
                                    )
                                  }
                                  accept="image/png, image/jpeg, image/jpg, image/gif"
                                />
                                {/* --- INLINE ERROR MESSAGE FOR FOOTER IMAGE --- */}
                                {footerError && (
                                  <small className="text-danger fw-bold">
                                    {footerError}
                                  </small>
                                )}
                              </div>
                            ) : (
                              <input
                                className="form-control form-control-sm"
                                value={f.value || ""}
                                onChange={(e) =>
                                  updateEntryMetadata(
                                    "footers",
                                    f.id,
                                    e.target.value,
                                  )
                                }
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                /* LOCKED SUMMARY VIEW */
                <div className="animate-fade-in">
                  <div className="setup-summary-line">
                    <span className="setup-summary-label">Project:</span>
                    <span className="fw-bold text-primary">{entryProject}</span>
                  </div>

                  <div className="setup-summary-line">
                    <span className="setup-summary-label">Mix Name:</span>
                    <span className="fw-bold text-dark">
                      {entryMix || "None"}
                    </span>
                  </div>

                  <div className="setup-summary-line d-none d-lg-block">
                    <span className="setup-summary-label">Headers:</span>
                    <span className="text-dark">
                      {formatMetadataSummary(entryHeaders)}
                    </span>
                  </div>
                  <div className="setup-summary-line mb-0 d-none d-lg-block">
                    <span className="setup-summary-label">Footers:</span>
                    {/* Updated to show image thumbnail in summary */}
                    <span className="text-dark">
                      {entryFooters
                        .filter((f) => f.value)
                        .map((f, idx) => (
                          <span
                            key={idx}
                            className="me-2 d-inline-flex align-items-center"
                          >
                            {f.type === "image" ? (
                              <img
                                src={f.value}
                                alt="Sig"
                                style={{
                                  height: "15px",
                                  border: "1px solid #ccc",
                                  cursor: "pointer",
                                }}
                                onClick={() => setPreviewImage(f.value)}
                              />
                            ) : (
                              <span>{f.value}</span>
                            )}
                            {idx <
                              entryFooters.filter((x) => x.value).length -
                              1 && <span className="mx-1">|</span>}
                          </span>
                        ))}
                      {entryFooters.filter((f) => f.value).length === 0 &&
                        "None"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <CustomFormRenderer
            key={formKey}
            schema={{
              ...form.schema,
              components: [...(form.schema.components || []), ...customColumns],
            }}
            initialData={editingEntry || {}}
            onFormReady={handleFormReady}
          />
        </div>
        <div className="offcanvas-footer border-top p-2 bg-white d-flex justify-content-between align-items-center">
          {/* CANCEL BUTTON: Left Side, Red Filled Background (btn-danger) */}
          <button
            className="btn btn-danger btn-sm px-3 shadow-sm"
            onClick={closeOffcanvas}
          >
            Close
          </button>

          {/* SAVE BUTTON: Right Side, Blue Filled Background */}
          <button
            className="btn btn-primary btn-sm px-3 shadow-sm"
            onClick={handleSaveDraft}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : (offcanvasMode === "EDIT" ? "Update Entry" : "Save Draft")}
          </button>
        </div>
      </div>
      {showOffcanvas && (
        <div
          className="modal-backdrop fade show"
          style={{ zIndex: 1040 }}
          onClick={closeOffcanvas}
        ></div>
      )}

      <div
        className="bg-white border-bottom sticky-top py-3 shadow-sm"
        style={{ zIndex: 1030, top: "66px" }}
      >
        <div className="container px-4">
          {/* Row 1: Back Button + Project Title */}
          <div className="d-flex align-items-center gap-2 mb-1">
            <button
              onClick={handleNavigationBack}
              className="btn btn-white border shadow-sm rounded-circle d-flex align-items-center justify-content-center me-1"
              style={{ width: "35px", height: "35px", fontSize: "15px", flexShrink: 0 }}
            >
              <i className="bi bi-arrow-left"></i>
            </button>
            <h6 className="text-dark mb-0" style={headingStyle}>
              {form.title}
            </h6>
          </div>

          {/* Row 2 DESKTOP: Subtitle (left) + Filters (right) — never wraps */}
          <div className="d-none d-lg-flex align-items-center justify-content-between" style={{ flexWrap: "nowrap" }}>
            {/* Left: Entries List + Username */}
            <div
              className="d-flex align-items-center gap-2 text-muted"
              style={{ fontSize: "12px", flexShrink: 0 }}
            >
              <span>
                All Records
              </span>
              <span>•</span>
              <div className="d-flex align-items-center gap-1">
                <i className="bi bi-person text-success"></i>
                <span className="fw-medium">{userName}</span>
                <button
                  className="btn btn-link p-0 text-muted"
                  onClick={handleEditName}
                  title="Change User"
                >
                  <i
                    className="bi bi-pencil"
                    style={{ fontSize: "11px" }}
                  ></i>
                </button>
              </div>
            </div>

            {/* Right: Filters + View Report */}
            <div className="d-flex gap-2 align-items-center" style={{ flexShrink: 0 }}>
              <div className="d-flex align-items-center bg-white border rounded shadow-sm px-2" style={{ width: "180px", height: "31px" }}>
                <i className="bi bi-building text-muted"></i>
                <select
                  className="form-select border-0 shadow-none bg-transparent outline-none py-0 ps-2 no-border-override"
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  style={{ fontSize: "13px", cursor: "pointer" }}
                >
                  <option value="">All Projects</option>
                  {projectList.map((p, i) => (
                    <option key={i} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              <div className="d-flex align-items-center bg-white border rounded shadow-sm px-2" style={{ width: "180px", height: "31px" }}>
                <i className="bi bi-shuffle text-muted"></i>
                <select
                  className="form-select border-0 shadow-none bg-transparent outline-none py-0 ps-2 no-border-override"
                  value={selectedMix}
                  onChange={(e) => setSelectedMix(e.target.value)}
                  style={{ fontSize: "13px", cursor: "pointer" }}
                >
                  <option value="">All Mixes</option>
                  {mixList.map((mixName, i) => (
                    <option key={i} value={mixName}>{mixName}</option>
                  ))}
                </select>
              </div>

              <div style={{ width: "180px" }}>
                <PremiumDatePicker
                  value={selectedDate}
                  onChange={setSelectedDate}
                  placeholder="dd/mm/yyyy"
                />
              </div>

              </div>
            </div>
          </div>

          {/* Row 2 MOBILE: Subtitle */}
          <div className="d-lg-none ms-3 pe-3">
            <div
              className="d-flex align-items-center gap-2 text-muted mb-2"
              style={{ fontSize: "12px" }}
            >
              <span>
                All Records
              </span>
              <span>•</span>
              <div className="d-flex align-items-center gap-1">
                <i className="bi bi-person text-success"></i>
                <span className="fw-medium">{userName}</span>
                <button
                  className="btn btn-link p-0 text-muted"
                  onClick={handleEditName}
                  title="Change User"
                >
                  <i
                    className="bi bi-pencil"
                    style={{ fontSize: "11px" }}
                  ></i>
                </button>
              </div>
            </div>

            {/* MOBILE filters: Horizontal row (All 3 boxes in one line with equal dimensions) */}
            <div className="d-flex gap-2 w-100">
              <div className="d-flex align-items-center bg-white border rounded shadow-sm px-2" style={{ flex: "1 1 0", height: "31px", minWidth: 0 }}>
                <i className="bi bi-building text-muted"></i>
                <select
                  className="form-select border-0 shadow-none bg-transparent outline-none py-0 ps-2 text-truncate no-border-override"
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  style={{ fontSize: "13px", cursor: "pointer", width: "100%", border: "none", outline: "none", boxShadow: "none" }}
                >
                  <option value="">All Projects</option>
                  {projectList.map((p, i) => (
                    <option key={i} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              <div className="d-flex align-items-center bg-white border rounded shadow-sm px-2" style={{ flex: "1 1 0", height: "31px", minWidth: 0 }}>
                <i className="bi bi-shuffle text-muted"></i>
                <select
                  className="form-select border-0 shadow-none bg-transparent outline-none py-0 ps-2 text-truncate no-border-override"
                  value={selectedMix}
                  onChange={(e) => setSelectedMix(e.target.value)}
                  style={{ fontSize: "13px", cursor: "pointer", width: "100%", border: "none", outline: "none", boxShadow: "none" }}
                >
                  <option value="">All Mixes</option>
                  {mixList.map((mixName, i) => (
                    <option key={i} value={mixName}>{mixName}</option>
                  ))}
                </select>
              </div>

              <div style={{ flex: "1 1 0", minWidth: 0 }}>
                <PremiumDatePicker
                  value={selectedDate}
                  onChange={setSelectedDate}
                  placeholder="Pick Date"
                />
              </div>
            </div>
          </div>
      </div>

      <div className="container-fluid px-4 py-3">
        {isLoading ? (
          <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
            <div className="spinner-border text-success" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            <div className="animate-fade-in bg-white p-3 shadow-sm rounded-3" style={{ minHeight: "600px", overflowX: "auto" }}>
              <DynamicTable
                data={paginatedData}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                title="Master Report"
                formTitle={form.title}
                isReportMode={false}
                columnOrder={columnOrder}
                allComponents={allComponents}
                customColumns={customColumns}
                projectDetails={activeLayout}
                remarksKey={remarksKey}
                showRemarksColumn={showRemarksColumn}
                themeColor={reportThemeColor}
                onThemeColorChange={handleThemeColorChange}
                onUpdateEntry={handleCellUpdate}
                onDeleteRow={handleRequestDelete}
                onExportExcel={handleExportExcel}
                onExportPDF={handleExportPDF}
                onLayoutChange={handleLayoutChange}
                onAddColumn={() => setShowCalcModal(true)}
                onDeleteColumn={handleDeleteColumn}
                onColumnDrop={handleColumnDrop}
                includeHeaderFooter={includeHeaderFooter}
                onToggleHeaderFooter={handleToggleHeaderFooter}
                onEdit={handleInlineUpdate}
                onDelete={handleRequestDelete}
                onOpenOffcanvas={(row) => openOffcanvas(row)}
                onSaveInline={handleInlineCreate}
                onToggleCreatedBy={handleToggleCreatedBy}
                onToggleCreatedDate={handleToggleCreatedDate}
                includeCreatedBy={includeCreatedBy}
                includeCreatedDate={includeCreatedDate}
                submissionStatus={submissionNotification}
                onBatchSubmit={handleRequestSubmitAll}
                projectList={projectList}
                mixList={mixList}
                hasDrafts={hasDrafts}
                selectedProject={selectedProject}
                filteredEntries={filteredEntries}
              />
            </div>


          </>
        )}
      </div>

      {/* DELETE CONFIRMATION MODAL */}
      {/* DELETE CONFIRMATION MODAL - ADD THIS BLOCK */}
      {showDeleteModal && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1055 }}
        >
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content shadow-sm border-0 rounded-4">
              <div className="modal-body text-center p-4">
                <i className="bi bi-trash text-danger fs-3 mb-2"></i>
                <h6 className="fw-bold mb-1">Delete Entry?</h6>
                <p className="text-muted x-small mb-4">Are you sure?</p>
                <div className="d-flex gap-2 justify-content-center">
                  <button
                    className="btn btn-outline-secondary btn-sm px-3"
                    onClick={() => setShowDeleteModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-danger btn-sm px-3"
                    onClick={confirmDelete}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* BATCH SUBMIT REMARKS MODAL */}
      {showSubmitModal && (
        <div
          className="modal d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-bottom-0 pb-0">
                <h5 className="modal-title fw-bold">Finalize Batch</h5>
                <button
                  className="btn-close"
                  onClick={() => setShowSubmitModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-3">
                  You are about to submit all pending drafts. Please enter a
                  remark for this batch (e.g., "Weekly Report", "Site A
                  Completed").
                </p>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Enter batch remarks..."
                  value={batchRemarks}
                  onChange={(e) => setBatchRemarks(e.target.value)}
                ></textarea>
              </div>
              <div className="modal-footer border-top-0">
                <button
                  className="btn btn-light"
                  onClick={() => setShowSubmitModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-success fw-bold px-4"
                  onClick={handleFinalSubmitAll}
                  disabled={!batchRemarks.trim()}
                >
                  Confirm & Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- NEW: LIGHTBOX IMAGE PREVIEW --- */}
      {previewImage && (
        <div
          className="modal-backdrop d-flex align-items-center justify-content-center"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.85)",
            zIndex: 3000,
          }}
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="position-relative animate-fade-in"
            style={{ maxWidth: "90%", maxHeight: "90%" }}
          >
            <button
              className="btn btn-dark rounded-circle position-absolute top-0 end-0 m-3 shadow"
              onClick={() => setPreviewImage(null)}
              style={{ zIndex: 3001, width: "40px", height: "40px" }}
            >
              <i className="bi bi-x-lg"></i>
            </button>
            <img
              src={previewImage}
              alt="Full View"
              style={{
                maxWidth: "100%",
                maxHeight: "90vh",
                borderRadius: "8px",
                boxShadow: "0 10px 25px rgba(0,0,0,0.5)",
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FormEntryManager;
